const MAX_DIMENSION = 2048;

/**
 * Resize an image file to a maximum dimension while maintaining aspect ratio.
 * Returns a WebP blob (or JPEG fallback for older browsers).
 */
export async function resizeImageForUpload(file: File, maxDimension = MAX_DIMENSION): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;

  const scale = Math.min(1, maxDimension / Math.max(width, height));
  const targetWidth = Math.round(width * scale);
  const targetHeight = Math.round(height * scale);

  const canvas = new OffscreenCanvas(targetWidth, targetHeight);

  try {
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Failed to get canvas context");
    }

    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);

    // Try WebP first, fall back to JPEG for older Safari
    const webpBlob = await canvas.convertToBlob({ type: "image/webp" });
    if (webpBlob.type === "image/webp") {
      return webpBlob;
    }

    return canvas.convertToBlob({ type: "image/jpeg" });
  } finally {
    bitmap.close();
  }
}
